import React, { useState } from "react";
import { Download, Trash2, FolderInput, Loader2, AlertCircle, Eye } from "lucide-react";
import { fileUrl } from "@/lib/api";

export default function ImageCard({ image, status, onDownload, onDelete, onMove, onView }) {
  const [hover, setHover] = useState(false);

  if (status === "pending") {
    return (
      <div className="aspect-square rounded-sm border border-zinc-200 bg-zinc-50 flex flex-col items-center justify-center text-zinc-400 text-xs"
        data-testid="image-card-pending">
        <Loader2 className="w-6 h-6 animate-spin mb-2" strokeWidth={1.5} />
        Generating...
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="aspect-square rounded-sm border border-red-200 bg-red-50 p-4 flex flex-col items-center justify-center text-red-600 text-xs text-center"
        data-testid="image-card-error">
        <AlertCircle className="w-6 h-6 mb-2" strokeWidth={1.5} />
        <div className="font-medium">{image?.error || "Generation failed"}</div>
      </div>
    );
  }

  return (
    <div
      className="image-card relative aspect-square rounded-sm border border-zinc-200 bg-white overflow-hidden group cursor-zoom-in"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onView?.(image)}
      data-testid={`image-card-${image.id}`}
    >
      <img
        src={fileUrl(image.url)}
        alt={image.keyword}
        className="w-full h-full object-cover"
        loading="lazy"
      />

      {/* Hover actions overlay */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center gap-2 transition-opacity ${
          hover ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-2 flex-wrap justify-center">
          <button
            onClick={(e) => { e.stopPropagation(); onView?.(image); }}
            className="bg-white text-zinc-900 px-3 py-2 rounded-sm text-xs font-medium hover:bg-zinc-100 flex items-center gap-1.5"
            data-testid={`view-image-${image.id}`}
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={2} /> View
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDownload?.(image); }}
            className="bg-white text-zinc-900 px-3 py-2 rounded-sm text-xs font-medium hover:bg-zinc-100 flex items-center gap-1.5"
            data-testid={`download-image-${image.id}`}
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2} /> Download
          </button>
          {onMove && (
            <button
              onClick={(e) => { e.stopPropagation(); onMove?.(image); }}
              className="bg-white text-zinc-900 px-3 py-2 rounded-sm text-xs font-medium hover:bg-zinc-100 flex items-center gap-1.5"
              data-testid={`move-image-${image.id}`}
            >
              <FolderInput className="w-3.5 h-3.5" strokeWidth={2} /> Move
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete?.(image); }}
              className="bg-red-600 text-white px-3 py-2 rounded-sm text-xs font-medium hover:bg-red-700 flex items-center gap-1.5"
              data-testid={`delete-image-${image.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
        <div className="text-white/90 text-[10px] font-mono uppercase tracking-widest mt-2 text-center px-2">
          {[image.style, image.demographic, image.ethnicity, image.language?.toUpperCase()].filter(Boolean).join(" · ")} · v{image.variation_number}
        </div>
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-white/95 backdrop-blur border-t border-zinc-200 text-xs">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-zinc-900 truncate flex-1">{image.keyword}</div>
          {image.language && (
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex-shrink-0 px-1 py-0.5 bg-zinc-100 rounded-sm">
              {image.language}
            </span>
          )}
        </div>
        <div className="font-mono text-[10px] text-zinc-500 truncate">
          v{image.variation_number} · {image.style}{image.demographic ? ` · ${image.demographic}` : ""}
        </div>
      </div>
    </div>
  );
}
